import { ICategory } from '@Hotels-at-Home/hah-fe-core/dist/types/category';
import { find, get } from 'lodash';
import { IBaseProduct } from '@Hotels-at-Home/hah-fe-core/dist/types';

export const homepageHrefLangTags = [
  {
    hrefLang: 'en-gb',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'en-ie',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'en',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
  {
    hrefLang: 'es',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/es`,
  },
  {
    hrefLang: 'fr',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/fr`,
  },
  {
    hrefLang: 'de',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/de`,
  },
  {
    hrefLang: 'x-default',
    href: `${process.env.NEXT_PUBLIC_SITE_URL}/en`,
  },
];

export const hrefOrder = ['en-ie', 'en-gb', 'en', 'fr', 'de', 'es', 'x-default'];

interface Map {
  [key: string]: string | undefined;
}

export interface HrefLink {
  region: string;
  links: LinkItem[];
}

export interface LinkItem {
  value: string;
  url: string;
  icon?: string;
}

export interface HrefMap {
  href: string;
  hreflang?: string;
  rel: string;
}

export const filterExternalHrefData = (hrefData: HrefMap[]): HrefMap[] =>
  hrefData.filter(
    (item: HrefMap) =>
      item.hreflang !== 'en-ie' &&
      item.hreflang !== 'en-gb' &&
      item.hreflang !== 'en' &&
      item.hreflang !== 'fr-fr' &&
      item.hreflang !== 'de-de' &&
      item.hreflang !== 'es-es',
  );

export const generateEuHrefData = (
  languageUrls: { href: string; value: string }[] | undefined,
  siteUrl: string,
  pathPrefix: string,
): { href: string; url: string; rel: string }[] => {
  if (!Array.isArray(languageUrls) || languageUrls.length === 0) {
    // Log explicit error to aid debugging and SEO impact awareness
    // Missing language URLs means we cannot generate alternate hreflang tags,
    // which can harm international SEO and geo-targeting for this page.
    console.error(
      '[hreflangs] generateEuHrefData: languageUrls is undefined or empty. Unable to build hreflang links. This will result in missing rel="alternate" hreflang tags for EU locales.',
      { siteUrl, pathPrefix, languageUrls },
    );
    return [];
  }
  const euHrefData: { href: string; url: string; rel: string }[] = [
    ...languageUrls.map(({ href, value }) => ({
      href,
      url: `${siteUrl}/${href}${pathPrefix}${value}`,
      rel: 'alternate',
    })),
  ];

  const enEuHref = euHrefData?.find(item => item.href === 'en')?.url;
  const frEuHref = euHrefData?.find(item => item.href === 'fr')?.url;
  const deEuHref = euHrefData?.find(item => item.href === 'de')?.url;
  const esEuHref = euHrefData?.find(item => item.href === 'es')?.url;

  euHrefData.push(
    {
      href: 'en-ie',
      url: enEuHref,
      rel: 'alternate',
    },
    {
      href: 'en-gb',
      url: enEuHref,
      rel: 'alternate',
    },
    {
      href: 'fr-fr',
      url: frEuHref,
      rel: 'alternate',
    },
    {
      href: 'de-de',
      url: deEuHref,
      rel: 'alternate',
    },
    {
      href: 'es-es',
      url: esEuHref,
      rel: 'alternate',
    },
    {
      href: 'x-default',
      url: enEuHref,
      rel: 'alternate',
    },
  );

  return euHrefData;
};

export const getHrefData = (
  item: IBaseProduct | ICategory,
  locale: string,
  useFallbacks = false,
) => {
  const hrefLangs: Map = {
    'de-de': item?.hreflang_de,
    'en-gb': get(item, 'hreflang_en_ie', null),
    'en-ie': useFallbacks
      ? get(item, 'hreflang_en_ie', null) ??
        get(item, 'hreflang_en', find(homepageHrefLangTags, { hrefLang: 'en' })?.href)
      : get(item, 'hreflang_en_ie', null) ?? get(item, 'hreflang_en', null),
    'fr-fr': item?.hreflang_fr,
    'es-es': item?.hreflang_es,
    'x-default': useFallbacks
      ? get(item, 'hreflang_en', null) ?? find(homepageHrefLangTags, { hrefLang: 'en' }).href
      : get(item, 'hreflang_en', null) ?? get(item, 'hreflang_x_default', null),
  };

  const hrefData: HrefMap[] = [];

  hrefOrder.forEach(key => {
    if (hrefLangs[key] !== null) {
      hrefData.push({
        href: hrefLangs[key],
        hreflang: key,
        rel: 'alternate',
      });
    }
  });
  return hrefData;
};

// TODO: I think this can be removed, but keeping in case of compile errors
export function extractHrefLinks(item: IBaseProduct | ICategory): LinkItem[] {
  const createLink = (link: string | null) => {
    if (!link) return '';
    return link.includes('?') ? `${link}&referrer=eu` : `${link}?referrer=eu`;
  };

  const usLink = createLink(
    get(item, 'hreflang_en_US', null) ??
      get(item, 'hreflang_en_us', null) ??
      find(homepageHrefLangTags, { hrefLang: 'en-us' })?.href,
  );

  return [
    {
      value: 'U.S.A.',
      url: usLink,
      icon: 'us',
    },
  ];
}

export function getHrefLinks(): HrefLink[] {
  return [];
}
